import { GitCompareArrows } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { DashboardChatThreadReplacementProposalResult } from '@shared/types/dashboard-chat';

export const ThreadReplacementProposalCard = ({
  result,
  resolving,
  resolutionInProgress,
  error,
  onConfirm,
  onReject,
}: {
  result: DashboardChatThreadReplacementProposalResult;
  resolving: boolean;
  resolutionInProgress: boolean;
  error: string | null;
  onConfirm: () => void;
  onReject: () => void;
}) => {
  const proposal = result.proposal;
  const pending = proposal.status === 'pending';
  const statusLabel =
    proposal.status === 'confirmed'
      ? 'Remplacement confirmé'
      : proposal.status === 'rejected'
        ? 'Décision conservée'
        : proposal.status === 'expired'
          ? 'Proposition expirée'
          : 'Remplacement proposé';

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-domino-prune/25 bg-domino-white shadow-sm">
      <div className="border-b border-domino-cardBorder bg-domino-lilac/45 px-3 py-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-domino-aubergine">
          <GitCompareArrows className="h-3.5 w-3.5" />
          {statusLabel}
        </span>
      </div>
      <div className="space-y-3 px-3 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-domino-inkSoft">
            Décision actuelle
          </p>
          <p className="mt-1 text-xs leading-5 text-domino-inkSoft">
            {result.sourceDecision.reason ?? result.sourceDecision.topicTitle}
          </p>
        </div>
        <div className="h-px bg-domino-cardBorder" aria-hidden="true" />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-domino-inkSoft">
            Nouvelle direction à réévaluer
          </p>
          <p className="mt-1 text-sm font-semibold leading-5 text-domino-ink">
            {proposal.replacementGoal}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-domino-inkSoft">
            Pourquoi
          </p>
          <p className="mt-1 text-xs leading-5 text-domino-inkSoft">{proposal.reason}</p>
        </div>
        <p className="rounded-xl border border-domino-cardBorder bg-domino-muted px-3 py-2 text-xs leading-5 text-domino-inkSoft">
          La confirmation remplace la décision épinglée puis demande une nouvelle
          évaluation au Decision Kernel. Elle n’exécute aucune action directement.
        </p>
        {pending && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={resolving}
              disabled={resolutionInProgress}
              onClick={onConfirm}
            >
              Confirmer le remplacement
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={resolutionInProgress}
              onClick={onReject}
            >
              Garder la décision
            </Button>
          </div>
        )}
      </div>
      {error && (
        <p className="border-t border-red-100 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
          {error}
        </p>
      )}
    </div>
  );
};
